import { addDays } from '~/calculations/calendar'
import { buildAthleteProfileRecord } from '~/models/athleteProfile'
import type { ValidationIssues } from '~/models/common'
import {
  DEFAULT_EVIDENCE_KINDS,
  defaultDirectionFor,
  needsBodyMeasurement,
  needsTrainingContext,
  orderDomains,
} from '~/models/domainPreset'
import {
  buildDomainGoalRecord,
  type DomainGoalRecord,
  type DomainIntention,
  type DomainPriority,
  type FitnessDomain,
  isDomainIntention,
  isDomainPriority,
  isFitnessDomain,
} from '~/models/fitnessDomain'
import { buildGoalRecord } from '~/models/goal'
import type { MeasurementSessionRecord } from '~/models/measurementSession'
import { NAVY_FORMULA_SITES } from '~/models/measurementSite'
import { buildNutritionPlanRecord, type NutritionPlanRecord } from '~/models/nutritionPlan'
import { buildPhaseRecord } from '~/models/phase'
import {
  buildProjectRecord,
  type DesiredOutcome,
  defaultProjectName,
  isDesiredOutcome,
} from '~/models/project'
import type { RouteOutcome, RouteRecommendation } from '~/models/routeRecommendation'
import { DB_SCHEMA_VERSION } from '~/models/schemaVersion'
import { buildStarterCommitments, type StarterCommitments } from '~/rules/plan/starterCommitment'
import { recommendRoute } from '~/rules/route/engine'
import type { RouteInputSnapshot } from '~/rules/route/snapshot'
import { isTrainingExperience } from '~/rules/route/snapshot'
import type { SolaDatabase } from '~/storage/db'
import { type StorageError, storageError } from '~/storage/errors'
import { notifyWritten } from '~/storage/invalidation'
import { getMeta, setMeta } from '~/storage/repositories/metaRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { getSettings } from '~/storage/repositories/settingsRepository'
import { META_KEY, STORE } from '~/storage/schema'
import { withTransaction } from '~/storage/transactions'
import {
  type CalendarDate,
  calendarDateFrom,
  type IsoTimestamp,
  toCalendarDate,
  toIsoTimestamp,
} from '~/types/dates'
import { newId, type ProjectId } from '~/types/ids'
import type { Centimetres } from '~/types/units'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { deriveSnapshot, type MeasurementIssue, summariseSites } from './measurementService'
import { MAX_PROJECT_START_DAYS_AHEAD } from './projectService'

/**
 * Project Setup (D-062, D-063).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  SETUP ASKS FOR A DESTINATION. IT NEVER ASKS FOR A ROUTE.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * There is no "cut / maintain / lean bulk / recomp" question here, and there
 * must never be one: that is precisely the question the user cannot answer and
 * the reason the route engine exists. They describe where they want to get to;
 * Project Sola works out how, explains it, and they confirm it.
 *
 * Two invariants govern everything below.
 *
 * **Nothing is written until the final confirmation.** D-052 requires profile,
 * phase and goal to be validated and built first, then written in one
 * transaction leaving zero records on failure. Project Setup adds the project
 * and the first measurement session to that transaction rather than weakening
 * it — a half-finished setup must not leave a stray measurement behind.
 *
 * **A five-step wizard has to survive a reload**, because iOS will evict a
 * backgrounded tab. The draft therefore persists to `meta` on each step
 * transition, where operational state already lives (D-027). It is cleared
 * inside the same transaction that commits the setup, so a completed setup can
 * never leave a stale draft behind it.
 */

/**
 * What the wizard holds while it is being filled in.
 *
 * Every field is the raw string the user typed. Nothing is parsed, converted or
 * validated on the way in: a draft is a record of an unfinished form, and
 * coercing it would mean a half-typed height silently becoming `NaN` between
 * two steps.
 */
export interface ProjectSetupDraft {
  /**
   * The step to resume on, as a **key** rather than an index (D-090).
   *
   * **The step being moved TO, never the one being left.** Recording the step
   * behind the user sends them back one on every resume — the defect the weekly
   * wizard found at Checkpoint 2.1 (D-073), and the same shape of bug here.
   *
   * A key and not a number, for the reason Checkpoint 2.2 proved when it inserted
   * a step into the weekly wizard: the step list is now **computed from the
   * chosen domains**, so an index means something different for a cardio-only
   * user than for a body-composition one, and a draft written before a domain was
   * paused would resume on an unrelated step.
   */
  readonly stepKey: SetupStep
  /**
   * The domains the user chose, as raw keys.
   *
   * Unvalidated like every other draft field — a draft is a record of an
   * unfinished form. An unrecognised key is dropped when the list is read, not
   * when it is written, so a draft from a build with a fifth domain does not
   * break a resume.
   */
  readonly selectedDomains: readonly string[]
  /** Whether the selection came from a preset, which adds the confirm step. */
  readonly usedPreset: boolean
  /** Intention per domain key. Absent means the user has not answered yet. */
  readonly intentions: Readonly<Record<string, string>>
  /** Priority per domain key. */
  readonly priorities: Readonly<Record<string, string>>
  readonly heightCm: string
  readonly dateOfBirth: string
  /** Three readings, as typed. Exactly three are required (D-045). */
  readonly waistReadings: readonly string[]
  readonly neckReadings: readonly string[]
  readonly trainingExperience: string
  readonly weeklySessions: string
  /**
   * How long a typical session runs, as typed. **Optional, and blank is a real
   * answer** (D-101 §3).
   *
   * It exists so a starter plan can carry `PlannedItem.minutes`, and it is the
   * only reason it is collected — D-060's converse forbids asking for a field
   * nothing consumes. A blank produces planned items with a session count and no
   * duration, which is honest; supplying a default so the item looked complete
   * would be a number from nowhere, which is exactly what D-101 §3 forbids.
   */
  readonly minutesPerSession: string
  readonly desiredOutcome: string
  /**
   * The date the user is working towards.
   *
   * For `prepareForEvent` this **is** the event date — the wizard asks once.
   * Two dates in one flow read as a mistake, which a real tester said out loud
   * at Checkpoint 1.8, and there is no case where the event and the target
   * differ for a project aimed at that event.
   */
  readonly targetDate: string
  /**
   * When the project begins, as typed. **Blank means today** (D-107 §7).
   *
   * This is the one draft field where blank is a real default rather than an
   * unanswered question, and it contradicts `nutritionSource` above on purpose.
   * It is defensible here because there is a genuine default — today, which is
   * exactly what the app did before this field existed — and no third state.
   *
   * **It is deliberately not prefilled with today's date.** A draft is written on
   * every step transition and resumed days later, which is the whole reason it
   * exists, so a prefilled date goes stale and lands in the past — failing its
   * own `min` on the resume. Blank cannot go stale.
   */
  readonly startDate: string
  /** The user's own name for the project. Blank until they change the suggestion. */
  readonly projectName: string
  /**
   * The nutrition plan source and its numbers, as typed (D-096).
   *
   * Raw strings like every other draft field, including the three numeric ones —
   * a half-typed calorie figure must not become `NaN` between two steps. They are
   * parsed once, in `buildSetupNutritionPlan`, where a bad value becomes a stated
   * validation issue rather than a silent zero.
   *
   * **Blank `nutritionSource` means the user has not answered yet**, which is not
   * the same as `'none'` — the answer "I have no nutrition target". A blank
   * writes no record at all; `'none'` writes one that says so.
   */
  readonly nutritionSource: string
  readonly nutritionProviderLabel: string
  readonly nutritionCalorieTarget: string
  readonly nutritionCalorieBasis: string
  readonly nutritionProteinTargetG: string
  readonly nutritionConfiguredRatePercent: string
  readonly nutritionDailyChecksEnabled: boolean
}

export const EMPTY_SETUP_DRAFT: ProjectSetupDraft = {
  stepKey: 'domains',
  selectedDomains: [],
  usedPreset: false,
  intentions: {},
  priorities: {},
  heightCm: '',
  dateOfBirth: '',
  waistReadings: ['', '', ''],
  neckReadings: ['', '', ''],
  trainingExperience: '',
  weeklySessions: '',
  minutesPerSession: '',
  desiredOutcome: '',
  targetDate: '',
  startDate: '',
  projectName: '',
  nutritionSource: '',
  nutritionProviderLabel: '',
  nutritionCalorieTarget: '',
  nutritionCalorieBasis: '',
  nutritionProteinTargetG: '',
  nutritionConfiguredRatePercent: '',
  nutritionDailyChecksEnabled: false,
}

/* ── The step list ──────────────────────────────────────────────────────── */

/**
 * Every step the wizard can show, in the order it shows them.
 *
 * **The list a given user sees is computed from their chosen domains** — see
 * `stepsFor`. This declaration is the superset and the ordering, and nothing may
 * reorder it locally: the "nearest earlier step" resume rule below depends on this
 * being the one place the order is stated.
 *
 * `nutrition` arrived at C6.4 with D-096, and it sits **after `destination`**: it
 * asks where the numbers come from, which is only a sensible question once the
 * user has said what they are aiming for.
 */
export const SETUP_STEPS = [
  'domains',
  'confirmPreset',
  'intentions',
  'aboutYou',
  'measurements',
  'photos',
  'training',
  'destination',
  'nutrition',
  'confirm',
] as const

export type SetupStep = (typeof SETUP_STEPS)[number]

export function isSetupStep(value: unknown): value is SetupStep {
  return typeof value === 'string' && (SETUP_STEPS as readonly string[]).includes(value)
}

/** Short titles for the progress line. Presentation only. */
export const SETUP_STEP_TITLES: Readonly<Record<SetupStep, string>> = {
  domains: 'What to work on',
  confirmPreset: 'Check the suggestion',
  intentions: 'How much for each',
  aboutYou: 'About you',
  measurements: 'Measurements',
  photos: 'Starting photos',
  training: 'Training',
  destination: 'Your destination',
  nutrition: 'Nutrition',
  confirm: 'Your plan',
}

/**
 * The steps this user actually sees.
 *
 * **This is the whole of C6.1's product change.** A cardio-only user is never
 * asked for a waist, a neck or a photo, because those steps are not in their list
 * — not because they are shown and skippable.
 *
 * `aboutYou` is **not** conditional, and that is a deliberate narrowing of the
 * plan. Height and date of birth are person-level facts rather than
 * body-composition evidence, and `AthleteProfileRecord` requires both; making them
 * optional would change the Navy estimate, the export's age field and every reader
 * of the profile. The exclusions the product actually asked for — waist, neck,
 * photos — are honoured exactly.
 *
 * **`nutrition` follows body composition** (D-096, C6.4). "Where do your calorie
 * and protein targets come from?" is a question about a deficit or a surplus, and
 * a cardio-only or mobility-only user has neither — asking them would be the same
 * mistake as asking them for a waist. The step itself is where a user says they
 * have **no** target, so it cannot be conditional on their having one; what is
 * conditional on the answer is everything inside it.
 */
export function stepsFor(draft: ProjectSetupDraft): readonly SetupStep[] {
  const domains = selectedDomainsOf(draft)
  const measures = needsBodyMeasurement(domains)

  return SETUP_STEPS.filter((step) => {
    if (step === 'confirmPreset') return draft.usedPreset
    if (step === 'intentions') return domains.length > 0
    if (step === 'measurements' || step === 'photos' || step === 'nutrition') return measures
    if (step === 'training') return needsTrainingContext(domains)
    return true
  })
}

/** The domains on the draft, dropping anything this build does not recognise. */
export function selectedDomainsOf(draft: ProjectSetupDraft): readonly FitnessDomain[] {
  return orderDomains(draft.selectedDomains.filter(isFitnessDomain))
}

/** Whether this setup will produce a phase, a route and a body-composition target. */
export function willPlanBodyComposition(draft: ProjectSetupDraft): boolean {
  return needsBodyMeasurement(selectedDomainsOf(draft))
}

/**
 * Where to resume, given a draft whose step may no longer exist.
 *
 * **The nearest earlier step that is still present**, never the first and never a
 * step past it. This is reachable in ordinary use: a user who reaches
 * `measurements`, goes back and removes body composition has a draft pointing at a
 * step their new selection does not include. Sending them to the beginning would
 * discard the sense of progress the draft exists to preserve; sending them
 * forward would skip a question their new selection needs answering.
 *
 * A draft whose key is not a step at all — written by a build with a step this one
 * does not have — falls back to the first step, because there is no position to
 * preserve.
 */
export function resumeStepFor(draft: ProjectSetupDraft): SetupStep {
  const available = stepsFor(draft)
  const first = available[0] ?? 'domains'
  if (available.includes(draft.stepKey)) return draft.stepKey

  const wanted = SETUP_STEPS.indexOf(draft.stepKey)
  if (wanted < 0) return first

  /* Walk the declared order backwards from the missing step and take the first
     one this user still has. `available` preserves `SETUP_STEPS` order, so the
     result is always at or before where they were. */
  for (let index = wanted - 1; index >= 0; index -= 1) {
    const candidate = SETUP_STEPS[index]
    if (candidate !== undefined && available.includes(candidate)) return candidate
  }
  return first
}

/**
 * The step order this wizard used before C6.1, for reading an older draft.
 *
 * A draft written by the released build holds `step: number` against these five
 * plus a confirmation at index 5. Dropping such a draft on the floor would send a
 * user who had already typed their measurements back to the beginning on the
 * update that shipped domains — an update they did not ask for and would read as
 * lost work.
 */
const LEGACY_STEP_ORDER: readonly SetupStep[] = [
  'aboutYou',
  'measurements',
  'photos',
  'training',
  'destination',
]

export async function loadProjectSetupDraft(
  db: SolaDatabase,
): Promise<Result<ProjectSetupDraft | undefined, StorageError>> {
  const stored = await getMeta<ProjectSetupDraft>(db, META_KEY.projectSetupDraft)
  if (!stored.ok) return stored
  if (stored.value === undefined) return ok(undefined)

  /* Merged over the empty draft rather than trusted whole. A draft written by
     an older build is missing whatever was added since, and a resumed wizard
     reading `undefined` where it expects a string would break on a field the
     user has not reached yet. */
  return ok(migrateSetupDraft({ ...EMPTY_SETUP_DRAFT, ...stored.value }))
}

/**
 * Bring a stored draft forward to the current shape (D-090).
 *
 * Pure, and separate from `loadProjectSetupDraft` so it can be tested against a
 * hand-written legacy draft without a database — which is the only way to check a
 * migration for a shape this build can no longer produce.
 *
 * **Two things move.** A numeric `step` becomes the matching key, and a draft that
 * predates domain selection is given body composition, because that is the only
 * thing the wizard that wrote it could have been setting up. Inventing a different
 * selection would change what the user was in the middle of doing.
 */
export function migrateSetupDraft(draft: ProjectSetupDraft): ProjectSetupDraft {
  const legacy = draft as ProjectSetupDraft & { readonly step?: unknown }
  const hasLegacyStep = typeof legacy.step === 'number'
  const carriesDomains = draft.selectedDomains.length > 0

  if (!hasLegacyStep && carriesDomains) return draft

  /* A pre-C6.1 draft was necessarily a body-composition setup: it is the only
     thing that wizard could produce. The intention and priority match what
     `backfillDomains` writes for an existing project, so an interrupted setup and
     a migrated project describe themselves the same way. */
  const selectedDomains = carriesDomains ? draft.selectedDomains : ['bodyComposition']
  const intentions = carriesDomains ? draft.intentions : { bodyComposition: 'improve' }
  const priorities = carriesDomains ? draft.priorities : { bodyComposition: 'primary' }

  const stepKey = hasLegacyStep
    ? (LEGACY_STEP_ORDER[legacy.step as number] ?? 'confirm')
    : draft.stepKey

  return {
    ...draft,
    stepKey: isSetupStep(stepKey) ? stepKey : 'domains',
    selectedDomains,
    intentions,
    priorities,
    /* Not a preset. The old wizard had no preset step, so claiming one would add a
       confirmation the user never triggered. */
    usedPreset: false,
  }
}

export async function saveProjectSetupDraft(
  db: SolaDatabase,
  draft: ProjectSetupDraft,
): Promise<Result<void, StorageError>> {
  return setMeta(db, META_KEY.projectSetupDraft, draft)
}

export async function clearProjectSetupDraft(
  db: SolaDatabase,
): Promise<Result<void, StorageError>> {
  return setMeta(db, META_KEY.projectSetupDraft, undefined)
}

export interface SetupValidationError {
  readonly kind: 'setupInvalid'
  readonly issues: readonly MeasurementIssue[] | ValidationIssues
}

/**
 * Setup was run while a project was already running (D-103 §7).
 *
 * Not a validation issue: nothing the user typed is wrong. It is a refusal, and
 * it exists because the fork it prevents is silent — `getActiveProject` sorts by
 * start date descending and takes the first, so two projects created on the same
 * day are resolved by whatever order IndexedDB returns. The loser is not
 * completed, not abandoned and no longer reachable, along with its
 * `recommendationSnapshot`, its weekly reviews and its domain goals.
 *
 * **Enforced in the service rather than by hiding the screen.** `#/onboarding` is
 * a reachable hash and "nothing links there" is a convention, not a guarantee.
 */
export interface ProjectAlreadyActiveError {
  readonly kind: 'projectAlreadyActive'
}

export type ProjectSetupError = SetupValidationError | ProjectAlreadyActiveError | StorageError

export function isSetupValidationError(error: ProjectSetupError): error is SetupValidationError {
  return error.kind === 'setupInvalid'
}

export function setupErrorMessage(error: ProjectSetupError): string {
  if (isSetupValidationError(error)) {
    return error.issues.map((issue) => issue.message).join(' ')
  }
  if (error.kind === 'projectAlreadyActive') {
    return 'You already have a project running, so Project Sola did not start another one. Finish or set aside the current project in Settings first.'
  }
  return error.message
}

/**
 * The measurement the route engine reads, summarised but not yet stored.
 *
 * Built here rather than by saving a session first, because nothing may reach
 * the database before the user confirms. The summarising itself is the shared
 * `summariseSites` — the median, the spread and the D-016 threshold warning are
 * not reimplemented for setup.
 */
export interface SetupMeasurement {
  readonly sites: ReturnType<typeof summariseSites> extends Result<infer T, infer _E> ? T : never
  readonly waistCm: number
  readonly neckCm: number | undefined
  readonly estimatedBodyFatPercent: number | undefined
}

/**
 * The readings half of a route input, named narrowly on purpose (D-103 §4).
 *
 * `ProjectSetupDraft` satisfies it structurally, so setup is unchanged, and the
 * phase-end re-run supplies the same two triples without constructing a fake
 * draft. The re-run **must** go through this rather than its own summariser: the
 * required-site list, the median and the D-016 spread threshold are one
 * definition, and a second one is how two screens start disagreeing about what
 * "required" means.
 */
export interface MeasurementReadings {
  readonly waistReadings: readonly string[]
  readonly neckReadings: readonly string[]
}

/**
 * The answers the route engine reads, beyond the measurement (D-103 §4).
 *
 * Narrowed for the same reason as `MeasurementReadings`. **`weeklySessions` is
 * the one that matters most here**: `applyTrainingFrequencyFloor` turns any lean
 * bulk into maintenance when it is absent, so a phase-end re-run that could not
 * supply it would be structurally incapable of ever recommending the transition
 * D-102 and D-103 exist to enable.
 */
export interface RouteAnswers {
  readonly desiredOutcome: string
  readonly targetDate: string
  readonly weeklySessions: string
  readonly trainingExperience: string
}

function readingsToNumbers(readings: readonly string[]): number[] {
  return readings
    .map((value) => value.trim())
    .filter((value) => value !== '')
    .map(Number)
}

/**
 * Summarise the entered readings, or report exactly what is wrong with them.
 *
 * **Waist and neck are both required at first run** (D-063), and this defers to
 * `summariseSites` for that rather than deciding it again — the required-site
 * list is one list, and setup diverging from the Review screen about it is how
 * two definitions of "required" start.
 *
 * That is not the same question as whether the *estimate* comes out. A waist at
 * or below the neck produces no Navy figure (D-014), and D-071 is explicit that
 * this does not block a route: waist-to-height is the primary evidence and
 * needs no neck at all. So a user can be routed with the estimate reported as
 * missing evidence — they simply cannot skip typing the readings in.
 */
export function summariseSetupMeasurement(
  draft: MeasurementReadings,
  heightCm: number,
  thresholdCm: Centimetres,
): Result<SetupMeasurement, readonly MeasurementIssue[]> {
  const waist = readingsToNumbers(draft.waistReadings)
  const neck = readingsToNumbers(draft.neckReadings)

  const summarised = summariseSites(
    { [NAVY_FORMULA_SITES.waist]: waist, [NAVY_FORMULA_SITES.neck]: neck },
    thresholdCm,
  )
  if (!summarised.ok) return err(summarised.error)

  const derived = deriveSnapshot(summarised.value, heightCm as Centimetres, undefined)
  const waistCm = summarised.value[NAVY_FORMULA_SITES.waist]?.medianCm

  if (waistCm === undefined) {
    return err([{ site: NAVY_FORMULA_SITES.waist, message: 'Enter three waist readings.' }])
  }

  return ok({
    sites: summarised.value,
    waistCm,
    neckCm: summarised.value[NAVY_FORMULA_SITES.neck]?.medianCm,
    estimatedBodyFatPercent: derived.bodyFatPercent,
  })
}

/**
 * Build the engine's input from the draft.
 *
 * Pure: the reference date and the measurement arrive as arguments, and the
 * result is one value the engine can be run against in a fixture test without a
 * database anywhere near it.
 */
export function buildRouteSnapshot(
  draft: RouteAnswers,
  measurement: SetupMeasurement,
  heightCm: number,
  now: Date,
  /**
   * The day the route actually begins (D-107 §7).
   *
   * An explicit parameter rather than a field on `RouteAnswers`, because
   * `nextPhaseService` used to build the answers and then override `reference`
   * afterwards — two ways to set one field. Both callers now pass it here.
   *
   * The duration, the week count and the approximate month all follow from this,
   * so a project created on the Thursday to start on the Sunday is measured from
   * the Sunday. One visible consequence is recorded in D-107 §7: D-071 routes
   * anything under eight weeks to maintenance, so a project 8.2 weeks from setup
   * and 7.8 weeks from its start correctly flips. That is the point, not a bug.
   */
  reference: CalendarDate,
): RouteInputSnapshot | undefined {
  const targetDate = toCalendarDate(draft.targetDate)
  if (targetDate === undefined || !isDesiredOutcome(draft.desiredOutcome)) return undefined

  const sessions = Number(draft.weeklySessions)

  return {
    reference,
    /* When the advice was computed, which is not when it applies. */
    evaluatedAt: toIsoTimestamp(now),
    desiredOutcome: draft.desiredOutcome,
    targetDate,
    waistCm: measurement.waistCm,
    heightCm,
    estimatedBodyFatPercent: measurement.estimatedBodyFatPercent,
    weeklySessions:
      draft.weeklySessions.trim() === '' || !Number.isFinite(sessions) ? undefined : sessions,
    trainingExperience: isTrainingExperience(draft.trainingExperience)
      ? draft.trainingExperience
      : undefined,
    /* The event and the target are the same date, asked once. The engine still
       takes them separately, because it must not assume a caller conflated
       them. */
    eventDate: draft.desiredOutcome === 'prepareForEvent' ? targetDate : undefined,
  }
}

export interface SetupRecommendation {
  readonly outcome: RouteOutcome
  readonly measurement: SetupMeasurement
  readonly snapshot: RouteInputSnapshot
}

/**
 * Run the route engine over the draft.
 *
 * Reads settings for the spread threshold and nothing else. **No AI is involved
 * and no future version may make it so** — the deterministic recommendation is
 * the only one Project Sola produces, and a manual second opinion comes after
 * it (D-008, D-062).
 */
export async function recommendRouteForSetup(
  db: SolaDatabase,
  draft: ProjectSetupDraft,
  now: Date,
): Promise<Result<SetupRecommendation, ProjectSetupError>> {
  const heightCm = Number(draft.heightCm)
  if (!Number.isFinite(heightCm) || heightCm <= 0) {
    return err({
      kind: 'setupInvalid',
      issues: [{ site: 'session', message: 'Enter your height before Project Sola can advise.' }],
    })
  }

  const settings = await getSettings(db, now)
  if (!settings.ok) return err(settings.error)

  const measurement = summariseSetupMeasurement(
    draft,
    heightCm,
    settings.value.circumferenceSpreadThresholdCm,
  )
  if (!measurement.ok) return err({ kind: 'setupInvalid', issues: measurement.error })

  /* The route runs from the day the project starts, not from the day the wizard
     was finished (D-107 §7). Safe to read here: the start date is collected on
     the destination step and this runs only on the way into `confirm`, a full
     step later. */
  const snapshot = buildRouteSnapshot(
    draft,
    measurement.value,
    heightCm,
    now,
    startDateFor(draft, calendarDateFrom(now)),
  )
  if (snapshot === undefined) {
    return err({
      kind: 'setupInvalid',
      issues: [
        { site: 'session', message: 'Choose what you are aiming for and the date you want it by.' },
      ],
    })
  }

  return ok({ outcome: recommendRoute(snapshot), measurement: measurement.value, snapshot })
}

export interface CompleteSetupInput {
  readonly draft: ProjectSetupDraft
  /**
   * The route, and the measurement it was derived from.
   *
   * **Both optional as of C6.1**, and they travel together: body composition is
   * the only domain with a route engine, so a cardio-only or mobility-only setup
   * has neither. Requiring them would have forced the wizard to invent a phase for
   * a user who never asked for one.
   */
  readonly recommendation?: RouteRecommendation
  readonly measurement?: SetupMeasurement
}

/**
 * The domain goals this setup will create.
 *
 * Pure, so the decision can be tested without a database, and shared by the
 * transaction below rather than rebuilt inside it.
 *
 * **A goal created here carries no numeric target, and a weekly commitment only
 * where every number in it came from the user** (D-101, amending what this
 * comment said until C6.6).
 *
 * The original prohibition and its reasoning were right and are worth keeping in
 * front of whoever reads this next: *writing a plausible-looking plan would
 * attribute coaching to Project Sola that it never did, which is D-060 in its
 * newest costume.* **What changed is not that the rule relaxed — it is that a third
 * option was found.** A starter built by splitting the session budget the user
 * themselves stated invents nothing; it hands their own answer back organised. A
 * session count or a duration Project Sola picked is still forbidden, still needs
 * an approved threshold, and still belongs to a decision that has not been taken.
 *
 * **Judgement did not move.** `DOMAIN_REGISTRY` still reports cardio, strength and
 * mobility as unimplemented, the goals still say `baselineRequired`, and the
 * confirmation screen still says so out loud — a plan is not a baseline and
 * proposing a week is not an opinion about one.
 */
/**
 * The starting weekly commitments this draft implies, keyed by domain (D-101).
 *
 * The adapter between a draft — every field a raw string, because a half-typed
 * number must not become `NaN` between two steps — and the pure allocator. **A
 * value that does not parse is dropped rather than coerced**: a bad session count
 * yields no plan at all, which is the same treatment `validateStep` gives it, and
 * is why a zero can never reach `PlannedItem.timesPerWeek`.
 */
export function buildSetupStarterCommitments(draft: ProjectSetupDraft): StarterCommitments {
  const intentions: Partial<Record<FitnessDomain, DomainIntention>> = {}
  const priorities: Partial<Record<FitnessDomain, DomainPriority>> = {}

  for (const domain of selectedDomainsOf(draft)) {
    const intention = draft.intentions[domain]
    const priority = draft.priorities[domain]
    if (intention !== undefined && isDomainIntention(intention)) intentions[domain] = intention
    if (priority !== undefined && isDomainPriority(priority)) priorities[domain] = priority
  }

  const minutes = Number(draft.minutesPerSession)

  return buildStarterCommitments({
    domains: selectedDomainsOf(draft),
    intentions,
    priorities,
    sessionsPerWeek: Number(draft.weeklySessions),
    minutesPerSession:
      draft.minutesPerSession.trim() === '' || !Number.isFinite(minutes) ? undefined : minutes,
  })
}

export function buildSetupDomainGoals(
  draft: ProjectSetupDraft,
  projectId: ProjectId,
  today: CalendarDate,
  timestamp: IsoTimestamp,
): Result<readonly DomainGoalRecord[], ValidationIssues> {
  const goals: DomainGoalRecord[] = []
  const starters = buildSetupStarterCommitments(draft)

  for (const domain of selectedDomainsOf(draft)) {
    const intention = draft.intentions[domain] ?? 'improve'
    const priority = draft.priorities[domain] ?? 'none'

    const built = buildDomainGoalRecord(
      {
        projectId,
        domain,
        intention,
        priority,
        direction: isDomainIntention(intention)
          ? defaultDirectionFor(intention)
          : /* Left as the raw value so validation reports it rather than a
               substitution that would hide the bad intention. */
            intention,
        /* Body composition is measured during setup, so its starting condition is
           on record the moment the project exists. Nothing else is, and saying so
           is more useful than a target nobody derived. */
        baseline: domain === 'bodyComposition' ? 'known' : 'baselineRequired',
        evidenceKinds: [...DEFAULT_EVIDENCE_KINDS[domain]],
        startedOn: today as string,
        /* Absent for every domain the user gave us nothing to derive one from —
           body composition, mobility, and anything they marked track-only. Spread
           conditionally rather than assigned `undefined`, so the field is genuinely
           missing: `exactOptionalPropertyTypes` treats the two as different, and
           an empty commitment would fail validation while an invented one would
           fail D-101. */
        ...(starters[domain] !== undefined ? { weeklyCommitment: starters[domain] } : {}),
      },
      timestamp,
    )
    if (!built.ok) return err(built.error)
    goals.push(built.value)
  }

  return ok(goals)
}

/**
 * The nutrition plan this setup will create, if any (D-096).
 *
 * Pure, and returns `undefined` rather than an empty record when the user never
 * reached or never answered the step. A row saying `source: 'none'` asserts "I
 * have no nutrition target", which is a real answer; a blank asserts nothing, and
 * writing one anyway would put a choice in the record the user did not make. This
 * is the same reasoning that leaves cardio with no domain-goal row (D-092).
 *
 * **`planKind` is derived, not asked.** A plan carrying a calorie or protein
 * figure is numeric and one carrying neither is not, so a separate question would
 * only create a way for the two to disagree.
 */
export function buildSetupNutritionPlan(
  draft: ProjectSetupDraft,
  projectId: ProjectId,
  today: CalendarDate,
  timestamp: IsoTimestamp,
): Result<NutritionPlanRecord | undefined, ValidationIssues> {
  const source = draft.nutritionSource.trim()
  if (source === '') return ok(undefined)

  const calories = optionalNumber(draft.nutritionCalorieTarget)
  const protein = optionalNumber(draft.nutritionProteinTargetG)
  const rate = optionalNumber(draft.nutritionConfiguredRatePercent)
  const label = draft.nutritionProviderLabel.trim()

  const built = buildNutritionPlanRecord(
    {
      projectId,
      source,
      planKind: calories === undefined && protein === undefined ? 'nonNumeric' : 'numeric',
      effectiveFrom: today as string,
      ...(label === '' ? {} : { providerLabel: label }),
      ...(calories === undefined ? {} : { calorieTarget: calories }),
      ...(calories === undefined ? {} : { calorieTargetBasis: draft.nutritionCalorieBasis }),
      ...(protein === undefined ? {} : { proteinTargetG: protein }),
      ...(rate === undefined ? {} : { configuredGoalRatePercent: rate }),
      /* Only meaningful for an external app, where the daily questions are off by
         default. Carried unconditionally so the record states the user's answer
         rather than the reader having to know which sources consult it. */
      ...(source === 'externalNutritionApp'
        ? { dailyChecksEnabled: draft.nutritionDailyChecksEnabled }
        : {}),
    },
    timestamp,
  )
  if (!built.ok) return err(built.error)
  return ok(built.value)
}

/**
 * A typed number, or nothing.
 *
 * A blank field is an unanswered question, never a zero — the same rule the rules
 * engine applies to a missing week. `NaN` is returned as `NaN` rather than
 * swallowed, so `validateNutritionPlan` reports the typo instead of this quietly
 * dropping the field the user thought they had filled in.
 */
function optionalNumber(value: string): number | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : Number(trimmed)
}

/**
 * Create the project the user just confirmed.
 *
 * **This is the explicit act D-020 requires.** The engine recommended; nothing
 * happened. Only a call to this function, from a button the user pressed on a
 * screen showing the reasoning, creates anything.
 *
 * Everything is validated and built before the transaction opens, and the whole
 * setup — profile, project, phase, goal, first measurement session, and the
 * removal of the draft — commits together or not at all.
 */
export async function completeProjectSetup(
  db: SolaDatabase,
  input: CompleteSetupInput,
  now: Date,
): Promise<Result<void, ProjectSetupError>> {
  const timestamp = toIsoTimestamp(now)
  const today = calendarDateFrom(now)
  /* The two dates below are different on purpose and the split is D-107 §3.
     `startDate` is when the project begins — it owns the project, the phase and
     the body-composition goal, because those are the plan. `today` is when the
     user was actually here — it owns the measurement session, the weigh-in, the
     photos, each domain goal's `startedOn` and the nutrition plan's
     `effectiveFrom`, because a measurement is evidence of a body on a day and
     moving it would record something that did not happen. */
  const { draft, recommendation, measurement } = input
  const startDate = startDateFor(draft, today)

  /* The fork guard (D-103 §7), first because everything below writes. A project
     runs until the user explicitly ends it (D-020), and ending a *phase* is not
     that — which is how the app came to create a second active project without
     ever asking. Refusing here makes it impossible rather than merely
     unreachable. */
  const existing = await getActiveProject(db)
  if (!existing.ok) return err(existing.error)
  if (existing.value !== undefined) return err({ kind: 'projectAlreadyActive' })

  /* The body-composition half of a setup is all-or-nothing: a route without the
     measurement it was derived from could not produce a session, and a measurement
     without a route could not produce a phase. Refusing the mismatch here means
     the branches below never have to ask about half of it. */
  const plansBody = willPlanBodyComposition(draft)
  if (plansBody && (recommendation === undefined || measurement === undefined)) {
    return err({
      kind: 'setupInvalid',
      issues: [{ site: 'session', message: 'The recommendation is missing. Start setup again.' }],
    })
  }

  const profile = buildAthleteProfileRecord(
    { heightCm: Number(draft.heightCm), dateOfBirth: draft.dateOfBirth },
    timestamp,
  )
  if (!profile.ok) return err({ kind: 'setupInvalid', issues: profile.error })

  const project = buildProjectRecord(
    {
      name: projectNameFor(draft, recommendation, today),
      /* The chosen start, which may be up to a week ahead (D-107). The blank is
         propagated deliberately, so a missing target date makes `validateProject`
         report both rather than only one. */
      startDate: draft.targetDate === '' ? draft.targetDate : (startDate as string),
      /* The day setup actually happened, which opens the evidence window so the
         project's own starting condition is inside its own summary (D-107 §3). */
      createdOn: today as string,
      targetDate: draft.targetDate,
      /* A desired outcome is a body-composition destination — the vocabulary is
         "visible abs", "lose belly fat". A cardio-only project has none, and
         inventing one would put words in the user's mouth about a domain they did
         not choose. The field is optional for exactly this reason (D-090). */
      ...(recommendation === undefined ? {} : { desiredOutcome: recommendation.desiredOutcome }),
      ...(recommendation?.desiredOutcome === 'prepareForEvent'
        ? { eventDate: draft.targetDate }
        : {}),
    },
    timestamp,
  )
  if (!project.ok) return err({ kind: 'setupInvalid', issues: project.error })

  const projectId = project.value.id

  /* An immutable value copy of what Project Sola actually advised, following
     the `phaseSnapshot` precedent (D-046). A later edit to the phase or the
     goal must never retroactively change the recommendation the user read and
     agreed to — which is exactly what recomputing it later would do. Absent when
     no route was produced, rather than an empty object standing in for one. */
  const projectRecord: typeof project.value =
    recommendation === undefined
      ? project.value
      : { ...project.value, recommendationSnapshot: recommendation }

  const domainGoals = buildSetupDomainGoals(draft, projectId, today, timestamp)
  if (!domainGoals.ok) return err({ kind: 'setupInvalid', issues: domainGoals.error })

  const body = plansBody
    ? buildBodyCompositionRecords({
        draft,
        // Checked above; both are present whenever `plansBody` holds.
        recommendation: recommendation as RouteRecommendation,
        measurement: measurement as SetupMeasurement,
        projectId,
        today,
        startDate,
        timestamp,
      })
    : ok(undefined)
  if (!body.ok) return err({ kind: 'setupInvalid', issues: body.error })

  const nutritionPlan = buildSetupNutritionPlan(draft, projectId, today, timestamp)
  if (!nutritionPlan.ok) return err({ kind: 'setupInvalid', issues: nutritionPlan.error })

  const written = await withTransaction(
    db,
    [
      STORE.profile,
      STORE.projects,
      STORE.domainGoals,
      STORE.phases,
      STORE.goals,
      STORE.measurementSessions,
      STORE.nutritionPlans,
      STORE.meta,
    ] as const,
    async (tx) => {
      await tx.objectStore(STORE.profile).put(profile.value)
      await tx.objectStore(STORE.projects).put(projectRecord)
      for (const goal of domainGoals.value) {
        await tx.objectStore(STORE.domainGoals).put(goal)
      }
      if (body.value !== undefined) {
        await tx.objectStore(STORE.phases).put(body.value.phase)
        await tx.objectStore(STORE.goals).put(body.value.target)
        await tx.objectStore(STORE.measurementSessions).put(body.value.session)
      }
      if (nutritionPlan.value !== undefined) {
        await tx.objectStore(STORE.nutritionPlans).put(nutritionPlan.value)
      }
      /* In the same transaction as the records it produced: a setup that
         succeeded must not leave a draft that would resume it again. */
      await tx.objectStore(STORE.meta).put({ key: META_KEY.projectSetupDraft, value: undefined })
    },
  )

  if (!written.ok) return err(written.error)

  notifyWritten(
    STORE.profile,
    STORE.projects,
    STORE.domainGoals,
    STORE.phases,
    STORE.goals,
    STORE.measurementSessions,
    STORE.nutritionPlans,
    STORE.meta,
  )
  return ok(undefined)
}

/**
 * The name, with a fallback that does not depend on a route.
 *
 * `defaultProjectName` takes the target date, so a project with no recommendation
 * still gets the same "Project October 2026" shape rather than an empty name.
 */
function projectNameFor(
  draft: ProjectSetupDraft,
  recommendation: RouteRecommendation | undefined,
  today: CalendarDate,
): string {
  const typed = draft.projectName.trim()
  if (typed !== '') return typed
  if (recommendation !== undefined) return recommendation.suggestedProject.name

  const target = toCalendarDate(draft.targetDate)
  return defaultProjectName(target ?? today)
}

interface BodyCompositionInput {
  readonly draft: ProjectSetupDraft
  readonly recommendation: RouteRecommendation
  readonly measurement: SetupMeasurement
  readonly projectId: ProjectId
  /** When the user was here. Stamps the measurement session (D-107 §3). */
  readonly today: CalendarDate
  /** When the project begins. Stamps the phase and the goal (D-107 §3). */
  readonly startDate: CalendarDate
  readonly timestamp: IsoTimestamp
}

interface BodyCompositionRecords {
  readonly phase: ReturnType<typeof buildPhaseRecord> extends Result<infer T, infer _E> ? T : never
  readonly target: ReturnType<typeof buildGoalRecord> extends Result<infer T, infer _E> ? T : never
  readonly session: MeasurementSessionRecord
}

/**
 * The three records only a body-composition project has.
 *
 * Split out so the transaction reads as "always these, plus these when body
 * composition is active" rather than as a branch inside every `put`. The phase,
 * the body-composition target and the first measurement session stand or fall
 * together — a phase with no starting measurement has nothing to judge its first
 * week against.
 */
function buildBodyCompositionRecords(
  input: BodyCompositionInput,
): Result<BodyCompositionRecords, ValidationIssues> {
  const { draft, recommendation, measurement, projectId, today, startDate, timestamp } = input

  /* The phase takes its type and its range from the recommendation, not from a
     radio group — D-063 removed manual phase selection precisely so that the
     registry stays the single authority on which phases exist (D-043).

     `startDate`, not `today`: D-034 makes the phase's first day week 1 day 1, so
     a phase running before its own project is incoherent (D-107 §3). */
  const phase = buildPhaseRecord(
    {
      projectId,
      type: recommendation.recommendedPhase,
      startDate: startDate as string,
      plannedEndDate: draft.targetDate,
      targetWeeklyChangeMinPercent: recommendation.weeklyTargetPercent.min,
      targetWeeklyChangeMaxPercent: recommendation.weeklyTargetPercent.max,
    },
    timestamp,
  )
  if (!phase.ok) return err(phase.error)

  /* The body-composition target's span is the project's, so it moves with the
     start date rather than with the day setup happened. */
  const target = buildGoalRecord(
    { projectId, startDate: startDate as string, targetDate: draft.targetDate },
    timestamp,
  )
  if (!target.ok) return err(target.error)

  /* `today`, deliberately. The measurement was taken on the day the user stood
     there, and stamping it with a start date days later would record a weigh-in
     on a day they did not weigh in (D-107 §3). `ProjectRecord.createdOn` is what
     keeps it inside the project's own evidence window. */
  const session: MeasurementSessionRecord = {
    id: newId<'MeasurementSessionId'>(),
    date: today,
    sites: measurement.sites,
    derived: deriveSnapshot(measurement.sites, Number(draft.heightCm) as Centimetres, undefined),
    phaseId: phase.value.id,
    recordedAt: timestamp,
    createdAt: timestamp,
    updatedAt: timestamp,
    schemaVersion: DB_SCHEMA_VERSION,
  }

  return ok({ phase: phase.value, target: target.value, session })
}

/** Today, for the wizard's date bounds. Kept here so the page reads no clock. */
export function setupToday(now: Date): CalendarDate {
  return calendarDateFrom(now)
}

/**
 * How far ahead a project may be scheduled to start (D-107 §7).
 *
 * Seven days, and the reason is the recommendation rather than the calendar: the
 * route is derived from the measurement taken at setup, and a week guarantees
 * that evidence is never stale on the day the route begins. It also covers the
 * case the field exists for exactly — the next occurrence of any weekday is at
 * most seven days out.
 *
 * **One number, re-exported rather than restated**, because Settings enforces the
 * same bound when a waiting project is rescheduled and two copies would drift.
 *
 * **Not a `rules/thresholds.ts` entry.** No rule reads it and it changes no
 * recommendation; it is a bound on a form field.
 */
export const MAX_START_DATE_DAYS_AHEAD = MAX_PROJECT_START_DAYS_AHEAD

/** The latest start date the wizard will accept, for the field's `max`. */
export function latestSetupStartDate(today: CalendarDate): CalendarDate {
  return addDays(today, MAX_START_DATE_DAYS_AHEAD)
}

/**
 * When the project begins: the user's answer, or today (D-107 §7).
 *
 * **Blank means today**, which keeps the field optional — and that is what lets
 * every existing browser test walk setup unchanged. See `ProjectSetupDraft.startDate`
 * for why a blank rather than a prefilled date.
 */
export function startDateFor(draft: ProjectSetupDraft, today: CalendarDate): CalendarDate {
  const chosen = toCalendarDate(draft.startDate)
  return chosen ?? today
}

export type { DesiredOutcome }
export { storageError }
